<script setup>
import {ref} from 'vue'
import { useRouter } from 'vue-router'
import Button from '@/components/Blocks/Buttons/Default.vue'
import FileInput from '@/components/Blocks/Buttons/File.vue'
import FileAnalyser from '@/lib/file_analyzer/analyzer.js'

const router = useRouter()

const query = ref(null)
const file_hover = ref(false)

const toast = ref({
  display: false,
  size: 0,
  duplicates: 0,
  error: false,
  message: '',
})

function submit() {
  if (query.value == null || query.value.trim() === '') return

  const result = FileAnalyser.analyze_ip(query.value)
  const items = result.ips

  if (items.length === 0) {
    toast.value.error = true
    toast.value.message = 'No valid IPs found in the input'
    toast.value.display = true

    setTimeout(() => {
      toast.value.display = false
      toast.value.error = false
      toast.value.message = ''
    }, 5000)
    return
  }

  if (items.length > 1000) {
    toast.value.error = true
    toast.value.message = `Too many IPs. Maximum allowed: 1000. You provided: ${items.length}`
    toast.value.display = true

    setTimeout(() => {
      toast.value.display = false
      toast.value.error = false
      toast.value.message = ''
    }, 5000)
    return
  }

  toast.value.size = items.length
  toast.value.duplicates = result.orig_count - items.length
  toast.value.display = true

  if (items.length === 1) {
    router.push({
      name: 'EnrichIpResults',
      query: { input: items[0] }
    })
  } else {
    router.push({
      name: 'EnrichIpTable',
      query: { input: items.join(',') }
    })
  }
}

function process_text(text) {
  let existingText = query.value || ''
  let existingItems = FileAnalyser.analyze_ip(existingText).ips
  let existingCount = existingItems.length

  let newResult = FileAnalyser.analyze_ip(text)
  let newItems = newResult.ips
  let newOrigCount = newResult.orig_count

  let allSet = new Set([...existingItems, ...newItems])
  let finalItems = [...allSet]
  let finalCount = finalItems.length

  let newUniqueAdded = finalCount - existingCount
  let duplicatesRemoved = newOrigCount - newUniqueAdded

  query.value = finalItems.join("\n")

  toast.value.size = newUniqueAdded
  toast.value.duplicates = duplicatesRemoved
  toast.value.display = true

  setTimeout(() => {
    toast.value.display = false
    toast.value.duplicates = 0
    toast.value.size = 0
  }, 3000)
}

function process_file(file) {
  const reader = new FileReader();
  reader.addEventListener('load', (event) => {
    process_text(event.target.result)
  });
  reader.readAsText(file);
}

function onDrop(e) {
  if (e.dataTransfer.files.length) {
    process_file(e.dataTransfer.files[0])
  } else {
    process_text(e.dataTransfer.getData("Text"))
  }
  file_hover.value = false
}

function paste(e) {
  if(e.clipboardData.files.length) {
    process_file(e.clipboardData.files[0]);
  } else {
    process_text((e.clipboardData || window.clipboardData).getData("text"))
  }
}

function clearInput() {
  query.value = ''
  toast.value.display = false
  toast.value.duplicates = 0
  toast.value.size = 0
  toast.value.error = false
  toast.value.message = ''
}

</script>

<template>
  <div class="box dark p-4 bordered d-flex column" :class="{file_hover: file_hover}" @drop.prevent="onDrop" @dragenter.prevent="file_hover = true" @dragover.prevent="file_hover = true" @dragleave.prevent="file_hover = false">
    <div style="flex: 1;">
      <textarea v-model="query" @paste.prevent="paste" data-guide="enrich-ip-textarea" placeholder="Paste from clipboard, upload, drag and drop, or select a file with IP addresses to analyze.
Supports structured and unstructured input."></textarea>
      <div v-if="toast.display" class="toas" :class="{error: toast.error}">
        <span v-if="toast.error">
          <i class="fa-solid fa-circle-exclamation me-2"></i>{{ toast.message }}
        </span>
        <span v-else>
          Added {{ toast.size }} unique IP address<span v-if="toast.size > 1">es</span><span v-if="toast.duplicates > 0"> (removed {{ toast.duplicates}} duplicate<span v-if="toast.duplicates > 1">s</span>)</span>
        </span>
      </div>
    </div>
    <div class="toolbar d-flex">
      <FileInput class="icon-button me-2" @file="process_file" title="Upload file">
        <font-awesome-icon icon="upload" />
      </FileInput>
      <button class="icon-button me-2" @click="clearInput" title="Clear input">
        <font-awesome-icon icon="trash" />
      </button>
      <Button class="ms-auto" @click="submit" purple data-guide="enrich-ip-submit">submit</Button>
    </div>
</div>
</template>

<style scoped>

textarea {
  width: 100%;
  height: 100%;
  background-color: transparent;
  color: white;
  padding: 1rem;
  outline: none;
  resize: none;
  border: none;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 1rem;
  line-height: 1.5;
}

textarea::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.file_hover {
  border: 1px solid #95f395 !important;
}

.toas {
  position: absolute;
  right: 1rem;
  top: 1rem;
  border-radius: 10px;
  background-color: white;
  padding: .5rem 1rem;
  background-color: rgb(30 37 47 / 38%);
  border: 1px solid #232A35;
}

.toas.error {
  background-color: rgba(220, 38, 38, 0.15);
  border: 1px solid rgba(220, 38, 38, 0.5);
  color: #fca5a5;
}

.icon-button {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: #6B7280;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}

.icon-button:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

.icon-button:active {
  transform: scale(0.95);
}

/* Override FileInput default styles when used as icon-button */
.icon-button.b {
  background: transparent;
  padding: 0;
}

.icon-button.b:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

</style>
